import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';

import AlertCircleIcon from './icons/AlertCircleIcon';
import BarChart3Icon from './icons/BarChart3Icon';
import ShieldCheckIcon from './icons/ShieldCheckIcon';
import TrendingUpIcon from './icons/TrendingUpIcon';
import DollarSignIcon from './icons/DollarSignIcon';
import ClockIcon from './icons/ClockIcon';

interface ProTreatmentSummaryDisplayProps {
  plan: any;
}

const StatCard: React.FC<{ icon: ReactNode; label: string; value: string; colorClass: string }> = ({ icon, label, value, colorClass }) => (
    <div className="bg-zinc-800/50 p-2.5 rounded-lg">
        <div className="flex items-start gap-2.5">
            <div className={`w-7 h-7 flex-shrink-0 rounded-md flex items-center justify-center ${colorClass} bg-opacity-10 text-opacity-90`}>
                {icon}
            </div>
            <div>
                <p className="text-xs text-zinc-400">{label}</p>
                <p className="text-sm font-semibold text-zinc-100 leading-tight">{value || 'N/A'}</p>
            </div>
        </div>
    </div>
);

const ProTreatmentSummaryDisplay: React.FC<ProTreatmentSummaryDisplayProps> = ({ plan }) => {
    if (!plan?.masterDiagnosticSummary) {
        return (
            <div className="bg-white/5 rounded-xl p-4 text-zinc-400 text-sm">
                Pro analysis data is not available or is in an invalid format.
            </div>
        );
    }

    const { masterDiagnosticSummary, smileDesignSpecification, summary } = plan;

    const complexityColor = {
        'Low': 'text-teal-400',
        'Moderate': 'text-yellow-400',
        'High': 'text-orange-400',
        'Extreme': 'text-red-500',
    }[masterDiagnosticSummary?.clinicalComplexity || ''] || 'text-zinc-400';

    const riskColor = {
        'Low': 'text-teal-400',
        'Medium': 'text-yellow-400',
        'High': 'text-red-500',
    }[masterDiagnosticSummary?.riskStratification || ''] || 'text-zinc-400';

    return (
        <motion.div 
            layout 
            className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
            <div className="text-center pb-3 border-b border-white/10">
                <p className="text-xs font-semibold tracking-[0.2em] text-amber-400/80">NEXUS AI PLAN</p>
                <h3 className="text-2xl text-white mt-1" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                    {smileDesignSpecification?.designTheme || 'Personalized Smile Design'}
                </h3>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
                <StatCard 
                    icon={<BarChart3Icon className="w-4 h-4"/>} 
                    label="Complexity" 
                    value={masterDiagnosticSummary?.clinicalComplexity}
                    colorClass={complexityColor}
                />
                <StatCard 
                    icon={<TrendingUpIcon className="w-4 h-4"/>} 
                    label="Potential" 
                    value={masterDiagnosticSummary?.transformationPotential}
                    colorClass="text-purple-400"
                />
                <StatCard 
                    icon={<ShieldCheckIcon className="w-4 h-4"/>} 
                    label="Risk" 
                    value={masterDiagnosticSummary?.riskStratification}
                    colorClass={riskColor}
                />
                 <StatCard 
                    icon={<DollarSignIcon className="w-4 h-4"/>} 
                    label="Investment" 
                    value={summary?.investmentMagnitude}
                    colorClass="text-green-400"
                />
            </div>

            {masterDiagnosticSummary?.primaryConcerns?.length > 0 && (
                <div className="pt-3 border-t border-white/10">
                    <h4 className="text-sm font-semibold text-zinc-300 flex items-center gap-2 mb-2">
                        <AlertCircleIcon className="w-4 h-4 text-amber-400"/>
                        Primary Diagnostic Concerns
                    </h4>
                    <ul className="list-disc list-inside space-y-1.5 pl-2 text-sm text-zinc-400 marker:text-teal-400/50">
                        {masterDiagnosticSummary.primaryConcerns.map((concern: string, index: number) => (
                            <li key={index}>{concern}</li>
                        ))}
                    </ul>
                </div>
            )}

            {summary?.timelineOverview && (
                <div className="pt-3 border-t border-white/10">
                    <div className="flex items-center justify-between bg-zinc-800/50 px-3 py-2.5 rounded-lg">
                        <div className="flex items-center gap-2">
                            <ClockIcon className="w-4 h-4 text-teal-400"/>
                            <p className="text-sm font-semibold text-zinc-300">Estimated Timeline</p>
                        </div>
                        <p className="text-sm font-bold text-white bg-teal-500/10 px-2 py-0.5 rounded">{summary.timelineOverview}</p>
                    </div>
                </div>
            )}
        </motion.div>
    );
};

export default ProTreatmentSummaryDisplay;